/**
 * Qué contorno dibujar para la zona buscada (FE-04).
 *
 * Google no tiene polígonos de localidades para la Argentina (la capa
 * `LOCALITY` está vacía en su tabla de cobertura); lo más fino que dibuja es
 * `ADMINISTRATIVE_AREA_LEVEL_2`: partidos en Buenos Aires, departamentos en
 * el resto del país. Arriba está `ADMINISTRATIVE_AREA_LEVEL_1`: provincias y
 * CABA. Las reglas:
 *
 * - **Partido / departamento**: si la zona se llama igual que su partido
 *   ("Tigre", "Rosario"). En la provincia de Buenos Aires, además, cualquier
 *   localidad pinta el partido que la contiene ("Ramos Mejía" pinta La
 *   Matanza, "Nordelta" pinta Tigre): ahí los partidos son chicos. En el
 *   interior un departamento puede ser mucho más grande que la localidad, así
 *   que "Funes" no pinta nada (sólo se encuadra, como con varias zonas).
 * - **CABA**: para Google es una entidad de nivel 1 y su nivel 2 son las
 *   comunas. Se pinta cuando la zona es CABA, un alias o "Buenos Aires" a
 *   secas (la ciudad). Los barrios porteños (Belgrano, Palermo) no pintan.
 * - **Provincia**: sólo cuando la zona es "Provincia de X", que es lo que
 *   manda el autocompletado al elegir una provincia. "Córdoba" a secas es la
 *   ciudad: trece provincias se llaman como su capital.
 *
 * Funciones puras, sin `google.maps`: reciben los campos del resultado del
 * Geocoder que necesitan.
 */

/** Lo que se usa de un `google.maps.GeocoderResult`. */
export interface ResultadoGeocoder {
  place_id: string;
  types: string[];
  address_components: { long_name: string; types: string[] }[];
}

/**
 * Capa de Google donde se pinta: partido / departamento (nivel 2) o
 * provincia / CABA (nivel 1).
 */
export type NivelContorno = 'partido' | 'provincia';

/** Lugar a contornear: su nivel y su nombre ya normalizado. */
export interface Objetivo {
  nivel: NivelContorno;
  /** Normalizado (`normalizarNombre`); para CABA, `CABA`. */
  nombre: string;
}

/** Nombre del objetivo cuando es CABA (se compara con `esNombreDeCaba`). */
const CABA = 'caba';

const NIVEL_1 = 'administrative_area_level_1';
const NIVEL_2 = 'administrative_area_level_2';

/**
 * Nombres de CABA ya normalizados: el de Google en castellano y en inglés y
 * los alias de escritura que también reconoce el backend
 * (`provincias.catalog.ts`). "Buenos Aires" a secas NO está: es también el
 * nombre de la provincia.
 */
const NOMBRES_CABA = new Set([
  'caba',
  'capital federal',
  'ciudad autonoma de buenos aires',
  'cdad. autonoma de buenos aires',
  'ciudad de buenos aires',
  'autonomous city of buenos aires',
]);

/**
 * Nombre comparable: sin acentos, en minúsculas y sin la palabra que Google
 * agrega al partido o a la provincia según el idioma ("Partido de Tigre",
 * "Tigre Partido", "Departamento Rosario", "Rosario Department", "Provincia
 * de Buenos Aires", "Buenos Aires Province").
 */
export function normalizarNombre(nombre: string): string {
  return nombre
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(
      /^(partido|departamento|department|provincia|province)( de(l)?)?\s+/,
      '',
    )
    .replace(/\s+(partido|departamento|department|provincia|province)$/, '')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * La zona llega como la eligió el usuario: el nombre solo ("Tigre") o la
 * etiqueta del autocompletado con la provincia ("Tigre, Buenos Aires").
 */
export function nombreDeZona(zona: string): string {
  return normalizarNombre(zona.split(',')[0] ?? '');
}

/** El resultado ES un partido / departamento (no una localidad adentro de uno). */
export function esPartido(resultado: ResultadoGeocoder): boolean {
  return resultado.types.includes(NIVEL_2);
}

/** Nombre del partido / departamento que contiene al resultado, si Google lo informa. */
export function partidoDe(resultado: ResultadoGeocoder): string | null {
  const componente = resultado.address_components.find((c) =>
    c.types.includes(NIVEL_2),
  );
  return componente?.long_name ?? null;
}

/** El nombre es uno de los de CABA (no el de la provincia de Buenos Aires). */
export function esNombreDeCaba(nombre: string): boolean {
  return NOMBRES_CABA.has(normalizarNombre(nombre));
}

/** Nombre de la provincia (o CABA) que contiene al resultado. */
function provinciaDe(resultado: ResultadoGeocoder): string | null {
  const componente = resultado.address_components.find((c) =>
    c.types.includes(NIVEL_1),
  );
  return componente?.long_name ?? null;
}

/** El resultado ES CABA entera (no un barrio ni una comuna adentro). */
function esCaba(resultado: ResultadoGeocoder): boolean {
  return (
    resultado.types.includes(NIVEL_1) &&
    esNombreDeCaba(provinciaDe(resultado) ?? '')
  );
}

/**
 * ¿La zona buscada es CABA? Sí cuando Google devolvió CABA misma, o cuando
 * devolvió la localidad "Buenos Aires" adentro de CABA (así llega desde el
 * autocompletado: "Buenos Aires, CABA") o la zona se escribió con un alias
 * ("Capital Federal"). Un barrio porteño también está adentro de CABA, pero
 * no es CABA: "Belgrano" no pinta nada.
 */
export function zonaEsCaba(
  zona: string,
  resultado: ResultadoGeocoder,
): boolean {
  if (!esNombreDeCaba(provinciaDe(resultado) ?? '')) return false;
  if (esCaba(resultado)) return true;
  const nombre = nombreDeZona(zona);
  return (
    esNombreDeCaba(nombre) ||
    (nombre === 'buenos aires' && resultado.types.includes('locality'))
  );
}

/**
 * Provincia de la zona "Provincia de X" / "Pcia. de X", normalizada ("Provincia
 * de Córdoba" → "cordoba"), o `null` si la zona no nombra una provincia así.
 */
export function provinciaBuscada(zona: string): string | null {
  const texto = zona.trim().toLowerCase();
  const prefijo = ['provincia de ', 'pcia. de ', 'pcia de '].find((p) =>
    texto.startsWith(p),
  );
  if (!prefijo) return null;
  return normalizarNombre(texto.slice(prefijo.length)) || null;
}

/**
 * El nombre de provincia de Google corresponde a la buscada. Admite que el de
 * Google siga después del nombre ("Tierra del Fuego, Antártida e Islas del
 * Atlántico Sur" para "Tierra del Fuego").
 */
function esLaProvincia(nombreGoogle: string, buscada: string): boolean {
  const nombre = normalizarNombre(nombreGoogle);
  return (
    nombre === buscada ||
    nombre.startsWith(`${buscada} `) ||
    nombre.startsWith(`${buscada},`)
  );
}

/**
 * Qué contornear para la zona, según el resultado de geocodificarla, o
 * `null` si no corresponde ningún contorno (ver las reglas arriba).
 */
export function objetivoDelContorno(
  zona: string,
  resultado: ResultadoGeocoder,
): Objetivo | null {
  const provinciaDelResultado = provinciaDe(resultado) ?? '';

  const provincia = provinciaBuscada(zona);
  if (provincia) {
    return esLaProvincia(provinciaDelResultado, provincia)
      ? { nivel: 'provincia', nombre: provincia }
      : null;
  }

  if (zonaEsCaba(zona, resultado)) return { nivel: 'provincia', nombre: CABA };

  const partido = partidoDe(resultado);
  if (!partido) return null;
  const nombre = normalizarNombre(partido);
  const enProvinciaDeBuenosAires = esLaProvincia(
    provinciaDelResultado,
    'buenos aires',
  );
  return esPartido(resultado) ||
    nombre === nombreDeZona(zona) ||
    enProvinciaDeBuenosAires
    ? { nivel: 'partido', nombre }
    : null;
}

/** El resultado ES el lugar a contornear (no algo adentro de él). */
export function esElObjetivo(
  resultado: ResultadoGeocoder,
  objetivo: Objetivo,
): boolean {
  if (objetivo.nivel === 'partido') {
    return (
      esPartido(resultado) &&
      normalizarNombre(partidoDe(resultado) ?? '') === objetivo.nombre
    );
  }
  if (!resultado.types.includes(NIVEL_1)) return false;
  const nombre = provinciaDe(resultado) ?? '';
  return objetivo.nombre === CABA
    ? esNombreDeCaba(nombre)
    : esLaProvincia(nombre, objetivo.nombre);
}

/**
 * De los resultados de una geocodificación inversa (todos los lugares que
 * contienen un punto, del más chico al más grande), el lugar a contornear.
 * Devuelve el resultado entero para que el mapa pueda encuadrar sus límites.
 */
export function buscarObjetivo<T extends ResultadoGeocoder>(
  resultados: T[],
  objetivo: Objetivo,
): T | null {
  return resultados.find((r) => esElObjetivo(r, objetivo)) ?? null;
}
